import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { TwinPresence } from '@cdf/api-client-react';
import type { PedestrianState } from '@/components/twin/pedestrian-simulation';

export interface LivePresenceCandidate {
  userId: string;
  displayName: string;
  avatarColor: string;
  accuracyMeters?: number | null;
  position: THREE.Vector3 | null;
}

function createLivePedestrian(candidate: LivePresenceCandidate): PedestrianState {
  const position = candidate.position!.clone();
  return {
    id: `live-presence:${candidate.userId}`,
    position,
    targetPosition: position.clone(),
    pathWaypoints: [],
    pathIndex: 0,
    pose: 'standing',
    arrivalPose: 'standing',
    speed: 1.5,
    color: candidate.avatarColor,
    animPhase: Math.random() * Math.PI * 2,
    role: 'visitor',
    label: candidate.displayName,
    livePresence: { userId: candidate.userId, accuracyMeters: candidate.accuracyMeters },
  };
}

/** Turns public presence records into persistent, smoothly interpolated 3D figures. */
export function useLivePresencePedestrians(
  participants: readonly TwinPresence[],
  projectParticipant: (participant: TwinPresence) => THREE.Vector3 | null,
  ownParticipant: LivePresenceCandidate | null,
): PedestrianState[] {
  const pedestriansRef = useRef(new Map<string, PedestrianState>());
  const [pedestrians, setPedestrians] = useState<PedestrianState[]>([]);

  useEffect(() => {
    const next = new Map<string, LivePresenceCandidate>();
    for (const participant of participants) {
      next.set(participant.userId, {
        userId: participant.userId,
        displayName: participant.displayName,
        avatarColor: participant.avatarColor,
        accuracyMeters: participant.accuracyMeters,
        position: projectParticipant(participant),
      });
    }
    if (ownParticipant) next.set(ownParticipant.userId, ownParticipant);

    for (const [userId, candidate] of next) {
      if (!candidate.position) continue;
      const existing = pedestriansRef.current.get(userId);
      if (existing) {
        existing.targetPosition.copy(candidate.position);
        existing.color = candidate.avatarColor;
        existing.label = candidate.displayName;
        existing.livePresence = { userId, accuracyMeters: candidate.accuracyMeters };
      } else {
        pedestriansRef.current.set(userId, createLivePedestrian(candidate));
      }
    }
    for (const userId of pedestriansRef.current.keys()) {
      if (!next.has(userId) || !next.get(userId)?.position) pedestriansRef.current.delete(userId);
    }
    setPedestrians([...pedestriansRef.current.values()]);
  }, [ownParticipant, participants, projectParticipant]);

  return pedestrians;
}
